import { motion } from "framer-motion";
import Image from "next/image";
import { useTranslation } from "next-i18next";
import { Cta } from "@/components/Cta";

const verticals = [
  {
    slug: "transport",
    title: "Transport",
    icon: "/icons/verticals/deep_transport_1.svg",
    tagline: "Prevent accidents before they happen",
    mainMetric: { value: "73%", label: "Faster Response" },
    supportingMetrics: [
      { value: "Real-time", label: "Incident Detection" },
      { value: "24/7", label: "Automated Monitoring" }
    ],
    outcomes: [
      "Highway traffic flow optimization",
      "Airport security breach prevention",
      "Urban congestion reduction",
      "Logistics route intelligence"
    ],
    color: "transport-mint"
  },
  {
    slug: "retail",
    title: "Retail",
    icon: "/icons/verticals/deep_retail_1.svg",
    tagline: "Intelligence that drives sales",
    mainMetric: { value: "32%", label: "Revenue Increase" },
    supportingMetrics: [
      { value: "98%", label: "Accuracy" },
      { value: "Zero", label: "New Hardware" }
    ],
    outcomes: [
      "Customer behavior pattern analysis",
      "Queue management optimization",
      "Theft detection and prevention",
      "Store layout effectiveness insights"
    ],
    color: "retail-coral"
  },
  {
    slug: "industry",
    title: "Industry",
    icon: "/icons/verticals/deep_industry_1.svg",
    tagline: "Automated compliance assurance",
    mainMetric: { value: "89%", label: "Fewer Incidents" },
    supportingMetrics: [
      { value: "100%", label: "PPE Coverage" },
      { value: "Instant", label: "Violation Alerts" }
    ],
    outcomes: [
      "PPE compliance monitoring",
      "Hazardous zone access control",
      "Equipment safety verification",
      "Automated incident documentation"
    ],
    color: "industry-lilac"
  }
];

const VerticalBenefits = () => {
  const { t } = useTranslation('home');

  return (
    <motion.section
      className="pt-20 md:pt-24 bg-background/50"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true, margin: "-50px" }}
    >
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            {t('verticalBenefits.title')}
          </h2>
          <p className="text-xl md:text-2xl text-secondary-foreground max-w-4xl mx-auto leading-relaxed">
            {t('verticalBenefits.subtitle')}
          </p>
        </motion.div>

        {/* Horizontal Layout - One per Row */}
        <div className="max-w-5xl mx-auto space-y-6">
          {verticals.map((vertical, index) => (
            <motion.div
              key={vertical.slug}
              className="group bg-container-background border border-border rounded-2xl overflow-hidden light_effect_card hover:border-opacity-60 transition-all duration-300"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + index * 0.15 }}
              viewport={{ once: true }}
            >
              <div className="grid md:grid-cols-[240px_1fr] gap-0">
                {/* Left: Hero Metric */}
                <div
                  className="relative p-8 flex flex-col justify-center items-center text-center overflow-hidden"
                  style={{
                    background: `linear-gradient(135deg, hsl(var(--${vertical.color}) / 0.15) 0%, hsl(var(--${vertical.color}) / 0.08) 100%)`
                  }}
                >
                  {/* Animated gradient overlay on hover */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `linear-gradient(135deg, hsl(var(--${vertical.color}) / 0.25) 0%, hsl(var(--${vertical.color}) / 0.12) 100%)`
                    }}
                  />

                  <div className="relative z-10">
                    <div className="text-xs uppercase tracking-wider font-semibold mb-3 text-secondary-foreground">
                      {t(`verticalBenefits.${vertical.slug}.title`)}
                    </div>
                    <div
                      className="text-4xl md:text-4xl font-bold mb-2"
                      style={{ color: `hsl(var(--${vertical.color}))` }}
                    >
                      {t(`verticalBenefits.${vertical.slug}.mainMetric.value`)}
                    </div>
                    <div className="text-sm font-medium text-primary-foreground">
                      {t(`verticalBenefits.${vertical.slug}.mainMetric.label`)}
                    </div>
                  </div>
                </div>

                {/* Right: Details */}
                <div className="p-8 space-y-6">
                  {/* Tagline */}
                  <div>
                    <p className="text-xl font-semibold text-primary-foreground mb-4">
                      {t(`verticalBenefits.${vertical.slug}.tagline`)}
                    </p>

                    {/* Supporting Metrics */}
                    <div className="flex gap-6">
                      {vertical.supportingMetrics.map((metric, idx) => (
                        <div key={idx} className="text-center">
                          <div
                            className="text-2xl font-bold"
                            style={{ color: `hsl(var(--${vertical.color}))` }}
                          >
                            {t(`verticalBenefits.${vertical.slug}.supportingMetrics.${idx}.value`)}
                          </div>
                          <div className="text-xs text-secondary-foreground uppercase tracking-wide">
                            {t(`verticalBenefits.${vertical.slug}.supportingMetrics.${idx}.label`)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Outcomes - Condensed List */}
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    {vertical.outcomes.map((outcome, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 text-sm"
                      >
                        <Image
                          src={`/icons/verticals/deep_${vertical.slug}_${idx + 1}.svg`}
                          alt={t(`verticalBenefits.${vertical.slug}.title`)}
                          width={16}
                          height={16}
                          className="w-4 h-4 flex-shrink-0 mt-0.5"
                        />
                        <span className="text-secondary-foreground leading-relaxed">
                          {t(`verticalBenefits.${vertical.slug}.outcomes.${idx}`)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="flex flex-col items-center mt-12 space-y-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <Cta size="large">
            {t('verticalBenefits.cta', { defaultValue: 'Get these results' })}
          </Cta>
          <p className="text-secondary-foreground text-sm">
            {t('verticalBenefits.disclaimer')}
          </p>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default VerticalBenefits;
